import { Box, Typography, Chip, Paper, IconButton, Select, MenuItem, TextField, Button } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { PersonRemove, Add, Edit as EditIcon } from "@mui/icons-material";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import type { SalespersonAssignmentProps } from "./SalespersonAssignment.types";
import { useSalespersonAssignmentViewModel } from "./SalespersonAssignment.vm";
import {
	GrowerCard,
	EntityCardMenu,
	UnassignedGrowersPanel,
	EntitySearchInput,
	type EntitySortOption,
} from "../shared";
import { PortalTooltip } from "../../../../../../helpers";

function SalespersonsHeader({ vm }: { vm: ReturnType<typeof useSalespersonAssignmentViewModel> }) {
	const isSearching = vm.searchText.trim().length > 0;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
			<Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>
				Salespersons{" "}
				{isSearching
					? `(${vm.filteredSortedSalespersons.length} / ${vm.totalSalespersonCount})`
					: `(${vm.totalSalespersonCount})`}
			</Typography>
			<EntitySearchInput
				value={vm.searchText}
				onChange={vm.setSearchText}
				placeholder="Search salespersons..."
				sx={{ ml: "auto" }}
			/>
			<Select
				size="small"
				value={vm.spSortOption}
				onChange={(e: SelectChangeEvent) => vm.setSpSortOption(e.target.value as EntitySortOption)}
				sx={{
					fontSize: "0.75rem",
					height: 28,
					minWidth: 0,
					"& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
				}}
			>
				<MenuItem value="default" sx={{ fontSize: "0.8rem" }}>
					Default
				</MenuItem>
				<MenuItem value="name-asc" sx={{ fontSize: "0.8rem" }}>
					Name A-Z
				</MenuItem>
				<MenuItem value="name-desc" sx={{ fontSize: "0.8rem" }}>
					Name Z-A
				</MenuItem>
				<MenuItem value="growers-desc" sx={{ fontSize: "0.8rem" }}>
					Growers High→Low
				</MenuItem>
				<MenuItem value="growers-asc" sx={{ fontSize: "0.8rem" }}>
					Growers Low→High
				</MenuItem>
			</Select>
			<PortalTooltip title="Unassign all growers from all salespersons">
				<IconButton size="small" onClick={vm.unassignAll}>
					<PersonRemove fontSize="small" />
				</IconButton>
			</PortalTooltip>
		</Box>
	);
}

function AddSalespersonCard({ vm }: { vm: ReturnType<typeof useSalespersonAssignmentViewModel> }) {
	if (!vm.isAddingNew) {
		return (
			<Paper
				variant="outlined"
				onClick={() => vm.setIsAddingNew(true)}
				sx={{
					p: 1.5,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 1,
					cursor: "pointer",
					borderStyle: "dashed",
					borderColor: "primary.light",
					bgcolor: "grey.50",
					"&:hover": {
						bgcolor: "primary.50",
						borderColor: "primary.main",
					},
					transition: "background-color 0.15s, border-color 0.15s",
					minHeight: 80,
				}}
			>
				<Add color="primary" />
				<Typography variant="subtitle2" color="primary">
					Add Salesperson
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper
			variant="outlined"
			sx={{
				p: 1.5,
				borderColor: "primary.main",
				borderWidth: 2,
			}}
		>
			<Typography variant="subtitle2" sx={{ mb: 1 }}>
				New Salesperson
			</Typography>
			<TextField
				size="small"
				label="Name"
				value={vm.newName}
				onChange={(e) => vm.setNewName(e.target.value)}
				fullWidth
				autoFocus
				sx={{ mb: 1 }}
				onKeyDown={(e) => {
					if (e.key === "Enter" && vm.newName.trim()) {
						vm.handleAddNewSalesperson();
					} else if (e.key === "Escape") {
						vm.handleCancelAddNew();
					}
				}}
			/>
			<TextField
				size="small"
				label="Phone (optional)"
				value={vm.newPhone}
				onChange={(e) => vm.handlePhoneChange(e.target.value)}
				error={!!vm.phoneError}
				helperText={vm.phoneError}
				fullWidth
				sx={{ mb: 1 }}
				onKeyDown={(e) => {
					if (e.key === "Enter" && vm.newName.trim()) {
						vm.handleAddNewSalesperson();
					} else if (e.key === "Escape") {
						vm.handleCancelAddNew();
					}
				}}
			/>
			<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
				<Button size="small" onClick={vm.handleCancelAddNew}>
					Cancel
				</Button>
				<Button size="small" variant="contained" disabled={!vm.newName.trim()} onClick={vm.handleAddNewSalesperson}>
					Add
				</Button>
			</Box>
		</Paper>
	);
}

export function SalespersonAssignment(props: SalespersonAssignmentProps) {
	const vm = useSalespersonAssignmentViewModel(props);

	return (
		<DragDropContext onBeforeCapture={vm.handleBeforeCapture} onDragEnd={vm.handleDragEnd}>
			<Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
				<Box sx={{ mb: 2 }}>
					<Typography variant="body2" color="text.secondary">
						{props.isSalespersonRequired
							? "Drag growers to assign their jobs to a salesperson. All growers must be assigned before proceeding."
							: "The jobs have been grouped by grower. Drag growers to assign their jobs to a salesperson. This step is optional — you can proceed without assigning all growers."}
					</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 400 }}>
					<UnassignedGrowersPanel
						unassignedGrowers={vm.unassignedGrowers}
						growerSortOption={vm.growerSortOption}
						setGrowerSortOption={vm.setGrowerSortOption}
						selectedGrowers={vm.selectedGrowers}
						clearSelection={vm.clearSelection}
						handleGrowerClick={vm.handleGrowerClick}
						draggingSelectedCount={vm.draggingSelectedCount}
						UNASSIGNED_DROPPABLE_ID={vm.UNASSIGNED_DROPPABLE_ID}
					/>

					{/* Right: Salesperson cards grid */}
					<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
						<SalespersonsHeader vm={vm} />
						<Box sx={{ flex: 1, overflowY: "auto" }}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
									gap: 1.5,
								}}
							>
								{vm.filteredSortedSalespersons.map((sp) => {
									const assignedGrowers = vm.growersByEntity.get(sp.id) || [];
									const isTemp = sp.id.startsWith("temp-");
									return (
										<Droppable key={sp.id} droppableId={sp.id}>
											{(provided, snapshot) => (
												<Paper
													ref={provided.innerRef}
													{...provided.droppableProps}
													variant="outlined"
													sx={{
														p: 1.5,
														bgcolor: snapshot.isDraggingOver ? "primary.50" : "background.paper",
														borderColor: snapshot.isDraggingOver ? "primary.main" : "divider",
														borderWidth: snapshot.isDraggingOver ? 2 : 1,
														transition: "border-color 0.15s, background-color 0.15s",
													}}
												>
													{/* Salesperson header */}
													{vm.editingSpId === sp.id ? (
														<Box sx={{ mb: 1 }}>
															<TextField
																size="small"
																label="Name"
																value={vm.editName}
																onChange={(e) => vm.setEditName(e.target.value)}
																fullWidth
																autoFocus
																sx={{ mb: 1 }}
																onKeyDown={(e) => {
																	if (e.key === "Enter" && vm.editName.trim()) {
																		vm.handleSaveEdit();
																	} else if (e.key === "Escape") {
																		vm.handleCancelEdit();
																	}
																}}
															/>
															<TextField
																size="small"
																label="Phone (optional)"
																value={vm.editPhone}
																onChange={(e) => vm.handleEditPhoneChange(e.target.value)}
																error={!!vm.editPhoneError}
																helperText={vm.editPhoneError}
																fullWidth
																sx={{ mb: 1 }}
																onKeyDown={(e) => {
																	if (e.key === "Enter" && vm.editName.trim()) {
																		vm.handleSaveEdit();
																	} else if (e.key === "Escape") {
																		vm.handleCancelEdit();
																	}
																}}
															/>
															<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
																<Button size="small" onClick={vm.handleCancelEdit}>
																	Cancel
																</Button>
																<Button
																	size="small"
																	variant="contained"
																	disabled={!vm.editName.trim()}
																	onClick={vm.handleSaveEdit}
																>
																	Save
																</Button>
															</Box>
														</Box>
													) : (
														<Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
															<Typography variant="subtitle2" sx={{ flex: 1 }}>
																{sp.name}
																{isTemp && (
																	<Chip
																		label="New"
																		size="small"
																		color="warning"
																		variant="outlined"
																		sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
																	/>
																)}
															</Typography>
															<Chip
																label={`${assignedGrowers.length} growers`}
																size="small"
																color={assignedGrowers.length > 0 ? "primary" : "default"}
																variant="outlined"
																sx={{ mr: 0.5 }}
															/>
															{isTemp && (
																<PortalTooltip title="Edit salesperson">
																	<IconButton size="small" onClick={() => vm.handleStartEdit(sp.id)} sx={{ mr: 0.25 }}>
																		<EditIcon fontSize="small" />
																	</IconButton>
																</PortalTooltip>
															)}
															<EntityCardMenu
																entityId={sp.id}
																onAssignAll={vm.assignAllToEntity}
																onAssignUnassigned={vm.assignUnassignedToEntity}
																onUnassignAll={vm.unassignAllFromEntity}
																selectedCount={vm.selectedGrowers.size}
																onAssignSelected={vm.assignSelectedToEntity}
															/>
														</Box>
													)}

													{/* Assigned growers */}
													<Box sx={{ minHeight: 36 }}>
														{assignedGrowers.length === 0 && !snapshot.isDraggingOver && (
															<Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
																Drop growers here
															</Typography>
														)}
														{assignedGrowers.map((grower, index) => (
															<GrowerCard
																key={grower.name}
																grower={grower}
																index={index}
																isSelected={vm.selectedGrowers.has(grower.name)}
																onToggleSelect={vm.handleGrowerClick}
																multiDragCount={vm.selectedGrowers.has(grower.name) ? vm.draggingSelectedCount : 0}
															/>
														))}
														{provided.placeholder}
													</Box>
												</Paper>
											)}
										</Droppable>
									);
								})}

								{/* Add New Salesperson card */}
								<AddSalespersonCard vm={vm} />
							</Box>
							{vm.filteredSortedSalespersons.length === 0 && vm.totalSalespersonCount > 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
									No salespersons match your search.
								</Typography>
							)}
							{vm.totalSalespersonCount === 0 && !vm.isAddingNew && (
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
									No salespersons available. Use the &quot;+&quot; card to create one.
								</Typography>
							)}
						</Box>
					</Box>
				</Box>
			</Box>
		</DragDropContext>
	);
}
